import { diContainer, serviceNames } from '../diContainer/diContainer';
import SaveNewBillingAddressUseCase from './SaveNewBillingAddressUseCase';
import SubmitNewsletterUseCase from './SubmitNewsletterUseCase';
import BillingRepository from '../repositories/BillingRepository';
import BopisStoresRepository from '../repositories/BopisStoresRepository';
import Config from '../domain/entities/Config';
import TransactionLoadHandler, { ITransactionLoad } from './TransactionLoadHandler';
import AddressFormStore from '../domain/entities/AddressFormStore';
import { BILLING_FORM_ERROR_SELECTED } from './ValidateAndSubmitAddressesUseCase';
import { FIELD_TYPE } from '../constants/Constants';

export const PURCHASER_FORM_ERROR = 'PURCHASER_FORM_ERROR';
export const PROXY_FORM_ERROR = 'PROXY_FORM_ERROR';
export const BILLING_FORM_ERROR = 'BILLING_FORM_ERROR';

const transactionLoadUseCaseOptions: ITransactionLoad = {
  transactionLoadOptions: {
    isEnabled: false,
  },
};

class SubmitBopisUseCase extends TransactionLoadHandler {
  private config: Config;

  private billingRepository: BillingRepository;

  private bopisStoresRepository: BopisStoresRepository;

  constructor() {
    super({
      isControlled: true,
      shouldAwait: true,
    });
    this.billingRepository = diContainer.get(serviceNames.billingRepository);
    this.bopisStoresRepository = diContainer.get(serviceNames.bopisStoresRepository);
    this.config = diContainer.get(serviceNames.config);
  }

  public async run(): Promise<void> {
    let error: string;

    const transaction = await this.transactionRepository.getTransaction();
    const {
      billingAddress,
    } = transaction;
    const { jsonSchemasEnabled } = this.config;

    if (billingAddress.isNewAddress && !this.billingRepository.formStore.isValid) {
      this.billingRepository.formStore.setShowErrors(true);
      error = BILLING_FORM_ERROR;
    }

    if (!this.bopisStoresRepository.bopisFormInfo.isValid) {
      const {
        purchaserFormStore,
        proxyFormStore,
      } = this.bopisStoresRepository.bopisFormInfo;
      purchaserFormStore.setShowErrors(!purchaserFormStore.isValid);
      proxyFormStore.setShowErrors(!proxyFormStore.isValid);
      if (!purchaserFormStore.isValid) {
        error = PURCHASER_FORM_ERROR;
      } else {
        error = PROXY_FORM_ERROR;
      }
    }

    // Validating a previously saved billing address against the schema. This block is added to address 
    // the corrupt data issue of some saved addresses not having all the details (OFP-960). 
    // Ex. Missing First & last names
    if (!billingAddress.isNewAddress) {
      // if json schema enabled need to load json schema for selected billing address validation
      if (jsonSchemasEnabled) {
        await this.billingRepository.loadAddressSchema(billingAddress.attributes[FIELD_TYPE.COUNTRY_ID]);
      }
      const billingValidationSchema = this.billingRepository.formStore.addressSchema;

      const isBillingAddressValid = new AddressFormStore(this.config)
        .setAddress(billingAddress)
        .validateAddressBySchema(billingValidationSchema);

      if (!isBillingAddressValid) {
        throw new Error(BILLING_FORM_ERROR_SELECTED);
      }
    }
    
    if (error) {
      throw new Error(error);
    }

    await this.bopisStoresRepository.setBopis();

    const saveNewBillingAddressUseCase = new SaveNewBillingAddressUseCase(transactionLoadUseCaseOptions);
    await saveNewBillingAddressUseCase.execute();

    const submitNewsletterUseCase = new SubmitNewsletterUseCase();
    await submitNewsletterUseCase.execute();

    await this.handleTransactionUpdate();
  }
}

export default SubmitBopisUseCase;
